/**
 * Abas — barra de navegação de seções, controlada. Sem dependência nova (Radix não está no
 * projeto); é um `tablist` acessível de botões. A seção ativa é estado de quem usa.
 *
 * **Segmento, não sublinhado.** Era um fio de 2px sob o texto ativo: o desenho de barra de abas de
 * 2013, e — o que pesa mais — um alvo que só se lê depois de procurar. Agora a aba ativa é uma
 * pílula preenchida sobre um trilho: o estado é a FORMA, não um detalhe de 2 pixels, e funciona
 * igual para quem enxerga pouco ou está numa tela ruim.
 *
 * Rola horizontalmente em telas estreitas em vez de quebrar linha, para a barra nunca empurrar
 * o conteúdo para baixo.
 *
 * **A aba aberta vai para a URL sozinha.** Não é uma boa prática que alguém precise lembrar de
 * seguir: quem monta uma barra de abas nova ganha o `#slug`, o F5 que volta na mesma aba e o
 * «voltar» que desfaz a troca, sem escrever uma linha para isso. Regra que depende de memória
 * funciona por três semanas — a próxima barra de abas simplesmente não teria, e ninguém notaria
 * até alguém reclamar que o link não abre onde devia.
 *
 * `ativa`/`onChange` continuam existindo para o caso raro de abas que NÃO devem mexer na URL —
 * dentro de um modal, por exemplo, onde a aba não é um lugar para onde se volta. Passar os dois
 * assume o controle; não passar nenhum deixa a URL mandar.
 */
import { useAbaNaUrl } from '@/lib/abaNaUrl';
import { cn } from '@/lib/utils';

export type Aba = { id: string; rotulo: string };

export function Abas({
  abas,
  ativa,
  onChange,
  className,
}: {
  abas: Aba[];
  ativa?: string;
  onChange?: (id: string) => void;
  className?: string;
}) {
  // Com o par controlado presente, o gancho recebe `undefined` e fica inerte — não lê nem
  // escreve a URL. Sem ele, a barra se governa pelo endereço.
  const controlado = ativa !== undefined && onChange !== undefined;
  const [naUrl, trocarNaUrl] = useAbaNaUrl(controlado ? undefined : abas);
  const abaAtiva = controlado ? ativa : naUrl;
  const trocar = controlado ? onChange : trocarNaUrl;

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      className={cn(
        'flex w-fit max-w-full gap-1 overflow-x-auto rounded-lg border border-border bg-surface-2 p-1',
        '[scrollbar-width:none]',
        className,
      )}
    >
      {abas.map((a) => {
        const ativo = a.id === abaAtiva;
        return (
          <button
            key={a.id}
            role="tab"
            type="button"
            aria-selected={ativo}
            onClick={() => trocar(a.id)}
            className={cn(
              'whitespace-nowrap rounded-md px-3.5 py-2 text-[14px] font-medium outline-none',
              'transition-colors duration-[var(--dur-fast)] ease-[var(--ease)]',
              'focus-visible:shadow-[var(--focus-ring)]',
              ativo
                ? 'bg-surface text-text shadow-[var(--realce-topo),var(--shadow-sm)]'
                : 'text-text-tertiary hover:bg-surface-hover hover:text-text-secondary',
            )}
          >
            {a.rotulo}
          </button>
        );
      })}
    </div>
  );
}
